/**
 * Multi-Provider RPC Client for MEMER Ecosystem (Vite+React)
 * Provides automatic failover across multiple RPC providers with health monitoring
 * This is the Vite equivalent of the Scaffold UI's MultiProviderRPC
 */

import { createProviderConfig, createHealthMonitoringConfig, validateProviderConfig, type RPCProviderConfig } from '../../api/RPCProviderFactory.js';

interface ProviderConfig {
    name: string;
    url: string | null;
    priority: number;
    rateLimitReset: number | null;
    isLocal: boolean;
    type?: string; // Add type field to track provider types (dev, node, mev)
}

interface ProviderStatus {
    status: 'healthy' | 'unhealthy' | 'unknown';
    lastSuccess?: number;
    lastFailure?: number;
    responseTime?: number;
    consecutiveFailures: number;
    lastError?: string;
}

interface ProviderStats {
    totalProviders: number;
    currentProvider: string;
    healthyProviders: number;
    unhealthyProviders: number;
    averageResponseTime: number;
}

export class MultiProviderRPC {
    private providers: ProviderConfig[];
    private currentProvider: number;
    private healthStatus: Map<string, ProviderStatus>;
    private requestCounts: Map<string, number>;
    private isLocalDevelopment: boolean;
    private lastUsedProvider: string;

    private getApiUrl(): string {
        // Use Vercel dev server in development, production API in production
        // This ensures environment-agnostic behavior
        if (import.meta.env.DEV) {
            // In development, Vite dev server proxies to Vercel dev server
            return '/api/rpc/proxy';
        } else {
            // In production, use the deployed Vercel API
            return '/api/rpc/proxy';
        }
    }

    constructor() {
        // Use centralized provider factory instead of hardcoded lists
        const factoryProviders = createProviderConfig();

        // Validate configuration
        if (!validateProviderConfig(factoryProviders)) {
            throw new Error('Invalid provider configuration from factory');
        }

        // Convert factory config to internal format
        this.providers = factoryProviders.map(provider => ({
            name: provider.name,
            url: provider.url,
            priority: provider.priority,
            rateLimitReset: null,
            isLocal: provider.type === 'dev',
            type: provider.type // Include type for dynamic MEV provider selection
        }));

        // Determine if we're in local development
        const chainId = String(import.meta.env.VITE_CHAIN_ID);
        this.isLocalDevelopment = chainId === '31337' || import.meta.env.VITE_FORK_ENABLED === 'true';

        this.currentProvider = 0;
        this.healthStatus = new Map();
        this.requestCounts = new Map();

        // Initialize lastUsedProvider based on environment
        if (this.isLocalDevelopment) {
            const anvilProvider = this.providers.find(p => p.isLocal);
            this.lastUsedProvider = anvilProvider ? anvilProvider.name : 'None';
        } else {
            this.lastUsedProvider = 'None';
        }

        // Initialize health monitoring
        this.startHealthMonitoring();

        console.log(`🏭 MultiProviderRPC initialized with ${this.providers.length} providers from factory`);
        console.log(`🏠 Local development mode: ${this.isLocalDevelopment}`);
        console.log(`🎯 Initial provider: ${this.lastUsedProvider}`);
    }

    async makeRequest(method: string, params: any[] = [], options: any = {}): Promise<any> {
        // STRICT LOCAL MODE: When chain ID is 31337, ONLY use Anvil provider
        if (this.isLocalDevelopment) {
            const anvilProvider = this.providers.find(p => p.isLocal);
            if (!anvilProvider) {
                throw new Error('Anvil provider not available in local development (chain ID 31337)');
            }

            console.log(`🔗 Using ${anvilProvider.name} provider: ${anvilProvider.url}`);

            try {
                const startTime = Date.now();
                const response = await this.callProvider(anvilProvider, method, params);
                this.recordSuccess(anvilProvider, Date.now() - startTime);
                return response;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                console.error(`❌ ${anvilProvider.name} provider failed: ${errorMsg}`);
                this.recordFailure(anvilProvider, error instanceof Error ? error : new Error('Unknown error'));
                throw new Error(`${anvilProvider.name} provider failed in local development: ${errorMsg}`);
            }
        }

        // PRODUCTION MODE: Use health-aware multi-provider failover
        const maxRetries = options.maxRetries || this.providers.length;
        let lastError: Error;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            // Use health-aware provider selection instead of simple rotation
            const provider = this.getNextHealthyProvider();

            try {
                const startTime = Date.now();
                const response = await this.callProvider(provider, method, params);

                // Track successful request
                this.recordSuccess(provider, Date.now() - startTime);
                return response;

            } catch (error) {
                lastError = error instanceof Error ? error : new Error('Unknown error');
                this.recordFailure(provider, lastError);

                // Handle rate limiting
                if (lastError.message.includes('429') || lastError.message.includes('rate limit')) {
                    this.handleRateLimit(provider, lastError);
                }

                // Health-aware selection will automatically skip this provider on next attempt
                console.log(`🔄 Provider ${provider.name} failed, health-aware selection will find next best option`);
            }
        }

        throw new Error(`All providers failed. Last error: ${lastError!.message}`);
    }

    private async callProvider(provider: ProviderConfig, method: string, params: any[]): Promise<any> {
        if (!provider.url) {
            throw new Error(`Provider ${provider.name} has no URL configured`);
        }

        // For proxy requests, include provider name in the request body
        const isProxyRequest = provider.url.includes('/api/rpc/proxy');
        const requestBody = isProxyRequest ? {
            jsonrpc: '2.0',
            id: Date.now(),
            method,
            params,
            provider: provider.name // Include provider name for proxy routing
        } : {
            jsonrpc: '2.0',
            id: Date.now(),
            method,
            params
        };

        const response = await fetch(provider.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
            signal: AbortSignal.timeout(10000) // 10 second timeout
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.error) {
            const error = new Error(data.error.message);
            (error as any).code = data.error.code;
            throw error;
        }

        return data.result;
    }

    private getCurrentProvider(): ProviderConfig {
        return this.providers[this.currentProvider];
    }

    private rotateProvider(): void {
        this.currentProvider = (this.currentProvider + 1) % this.providers.length;
    }

    /**
     * Get next healthy provider using health-aware selection
     * Prioritizes providers with no recent failures and skips known unhealthy ones
     */
    private getNextHealthyProvider(): ProviderConfig {
        const now = Date.now();
        const FAILURE_THRESHOLD = 3; // Skip providers with 3+ consecutive failures
        const FAILURE_TIMEOUT = 5 * 60 * 1000; // 5 minutes timeout for failed providers

        // First pass: Try providers with no recent failures
        for (let i = 0; i < this.providers.length; i++) {
            const providerIndex = (this.currentProvider + i) % this.providers.length;
            const provider = this.providers[providerIndex];
            const status = this.healthStatus.get(provider.name);

            if (!status || status.consecutiveFailures === 0) {
                this.currentProvider = providerIndex;
                return provider;
            }
        }

        // Second pass: Try providers with failures but within timeout
        for (let i = 0; i < this.providers.length; i++) {
            const providerIndex = (this.currentProvider + i) % this.providers.length;
            const provider = this.providers[providerIndex];
            const status = this.healthStatus.get(provider.name);

            if (status && status.consecutiveFailures < FAILURE_THRESHOLD) {
                this.currentProvider = providerIndex;
                return provider;
            }

            // Check if failure timeout has expired
            if (status && status.lastFailure && (now - status.lastFailure) > FAILURE_TIMEOUT) {
                console.log(`🔄 Retrying provider ${provider.name} after timeout`);
                this.currentProvider = providerIndex;
                return provider;
            }
        }

        // Fallback: Use least-failed provider if all are unhealthy
        let bestProvider = this.providers[0];
        let bestIndex = 0;
        let minFailures = Number.MAX_SAFE_INTEGER;

        for (let i = 0; i < this.providers.length; i++) {
            const provider = this.providers[i];
            const status = this.healthStatus.get(provider.name);
            const failures = status ? status.consecutiveFailures : 0;

            if (failures < minFailures) {
                minFailures = failures;
                bestProvider = provider;
                bestIndex = i;
            }
        }

        console.warn(`⚠️ All providers unhealthy, using least-failed: ${bestProvider.name} (${minFailures} failures)`);
        this.currentProvider = bestIndex;
        return bestProvider;
    }

    private recordSuccess(provider: ProviderConfig, responseTime: number): void {
        this.lastUsedProvider = provider.name; // Track which provider was actually used
        this.healthStatus.set(provider.name, {
            status: 'healthy',
            lastSuccess: Date.now(),
            responseTime,
            consecutiveFailures: 0
        });
    }

    private recordFailure(provider: ProviderConfig, error: Error): void {
        const current = this.healthStatus.get(provider.name) || { consecutiveFailures: 0 };
        this.healthStatus.set(provider.name, {
            ...current,
            status: 'unhealthy',
            lastFailure: Date.now(),
            lastError: error.message,
            consecutiveFailures: current.consecutiveFailures + 1
        });
    }

    private handleRateLimit(provider: ProviderConfig, error: Error): void {
        // Default to 1 minute from now if no specific reset time
        provider.rateLimitReset = Date.now() + 60000;
        console.warn(`⏰ Rate limit hit for ${provider.name}, reset at: ${new Date(provider.rateLimitReset)}`);
    }

    private startHealthMonitoring(): void {
        // Periodic health checks every 5 minutes (300 seconds)
        // Reduced frequency to minimize API calls and log noise
        setInterval(() => {
            this.performHealthChecks();
        }, 300000);
    }

    private async performHealthChecks(): Promise<void> {
        // Use factory for health monitoring configuration
        const factoryProviders = createHealthMonitoringConfig();
        const providersToMonitor = factoryProviders.map(provider => ({
            name: provider.name,
            url: provider.url,
            priority: provider.priority,
            rateLimitReset: null,
            isLocal: provider.type === 'dev',
            type: provider.type
        }));

        const healthPromises = providersToMonitor.map(async (provider) => {
            try {
                const startTime = Date.now();
                await this.callProvider(provider, 'eth_blockNumber', []);
                // Record health success without updating lastUsedProvider
                this.recordHealthSuccess(provider, Date.now() - startTime);
            } catch (error) {
                this.recordFailure(provider, error instanceof Error ? error : new Error('Unknown error'));
            }
        });

        await Promise.allSettled(healthPromises);
    }

    private getAllProvidersForMonitoring(): ProviderConfig[] {
        // Use factory for monitoring configuration
        const factoryProviders = createHealthMonitoringConfig();
        return factoryProviders.map(provider => ({
            name: provider.name,
            url: provider.url,
            priority: provider.priority,
            rateLimitReset: null,
            isLocal: provider.type === 'dev'
        }));
    }

    private recordHealthSuccess(provider: ProviderConfig, responseTime: number): void {
        // Same as recordSuccess but doesn't update lastUsedProvider (for health checks only)
        this.healthStatus.set(provider.name, {
            status: 'healthy',
            lastSuccess: Date.now(),
            responseTime,
            consecutiveFailures: 0
        });
    }

    // Public methods for the hook to use
    getHealthStatus(): Record<string, ProviderStatus> {
        const result: Record<string, ProviderStatus> = {};
        this.healthStatus.forEach((status, name) => {
            result[name] = status;
        });
        return result;
    }

    getProviderStats(): ProviderStats {
        // FIXED: Total providers should always be the number of providers being monitored
        // In local/fork development, we monitor ALL providers (DEV + NODE + MEV)
        // In production, we monitor only the providers we use
        const monitoredProviders = this.isLocalDevelopment ?
            this.getAllProvidersForMonitoring() : this.providers;

        const totalProviders = monitoredProviders.length;

        let healthyProviders = 0;
        let unhealthyProviders = 0;
        let totalResponseTime = 0;
        let responseTimeCount = 0;

        // Count health status for ALL monitored providers, not just the ones with status
        monitoredProviders.forEach((provider) => {
            const status = this.healthStatus.get(provider.name);

            if (status) {
                if (status.status === 'healthy') {
                    healthyProviders++;
                    if (status.responseTime) {
                        totalResponseTime += status.responseTime;
                        responseTimeCount++;
                    }
                } else {
                    unhealthyProviders++;
                }
            } else {
                // Providers without status are considered unknown/unhealthy
                unhealthyProviders++;
            }
        });

        const averageResponseTime = responseTimeCount > 0
            ? Math.round(totalResponseTime / responseTimeCount)
            : 0;

        return {
            totalProviders,
            currentProvider: this.lastUsedProvider || 'None',
            healthyProviders,
            unhealthyProviders,
            averageResponseTime
        };
    }

    // Convenience methods for common operations
    async getBalance(address: string): Promise<string> {
        return this.makeRequest('eth_getBalance', [address, 'latest']);
    }

    async getBlockNumber(): Promise<string> {
        return this.makeRequest('eth_blockNumber', []);
    }

    async call(transaction: any): Promise<string> {
        return this.makeRequest('eth_call', [transaction, 'latest']);
    }

    async sendTransaction(transaction: any): Promise<string> {
        // Skip MEV protection in local development
        if (this.isLocalDevelopment) {
            return this.makeRequest('eth_sendTransaction', [transaction]);
        }

        // DYNAMIC MEV PROTECTION: Use all available MEV providers from factory
        const mevProviders = this.providers.filter(p => p.type === 'mev').sort((a, b) => a.priority - b.priority);

        if (mevProviders.length > 0) {
            console.log(`🛡️ Using MEV protection for transaction (${mevProviders.length} providers available)`);

            // Try each MEV provider in priority order
            for (const mevProvider of mevProviders) {
                try {
                    console.log(`🔗 Trying MEV provider: ${mevProvider.name} (${mevProvider.url})`);
                    const result = await this.callProvider(mevProvider, 'eth_sendTransaction', [transaction]);
                    console.log(`✅ Transaction successful via ${mevProvider.name}`);
                    return result;
                } catch (error) {
                    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                    console.warn(`⚠️ MEV provider ${mevProvider.name} failed: ${errorMsg}`);
                    // Continue to next MEV provider
                }
            }

            console.warn('⚠️ All MEV providers failed, falling back to regular providers');
        } else {
            console.log('ℹ️ No MEV providers configured, using regular providers');
        }

        // Fallback to regular providers if MEV protection fails or unavailable
        return this.makeRequest('eth_sendTransaction', [transaction]);
    }

    async estimateGas(transaction: any): Promise<string> {
        return this.makeRequest('eth_estimateGas', [transaction]);
    }

    async getTransactionReceipt(hash: string): Promise<any> {
        return this.makeRequest('eth_getTransactionReceipt', [hash]);
    }

    async getTransactionCount(address: string): Promise<string> {
        return this.makeRequest('eth_getTransactionCount', [address, 'latest']);
    }
}

// Singleton instance
export const rpcProvider = new MultiProviderRPC();
